// API client for the event RSVP pages.
//
// The backend address comes from REACT_APP_API_BASE_URL (e.g. http://localhost:8081 for local
// development) and defaults to the production API the rest of the landing site uses.
const API_BASE = (process.env.REACT_APP_API_BASE_URL || "https://app.bloxsocial.live").replace(/\/+$/, "");

let tokenPromise = null;

/** The app-level JWT every API call needs (GET /token/jwt_token/), fetched once per page load. */
function getToken() {
  if (!tokenPromise) {
    tokenPromise = fetch(`${API_BASE}/token/jwt_token/`, { headers: { "Content-Type": "application/json" } })
      .then((r) => {
        if (!r.ok) throw new Error("Couldn't get an access token");
        return r.json();
      })
      .then((d) => d.token || d.jwt_token)
      .catch((e) => {
        tokenPromise = null; // try again next time
        throw e;
      });
  }
  return tokenPromise;
}

/** POST JSON (or FormData) and return { data, status }; throws an Error with .status/.data on failure. */
async function post(path, body) {
  const token = await getToken();
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}`, ...(isForm ? {} : { "Content-Type": "application/json" }) },
    body: body === undefined ? undefined : isForm ? body : JSON.stringify(body),
  });
  let data = null;
  try { data = await res.json(); } catch { /* empty body */ }
  if (!res.ok) {
    const err = new Error((data && (data.error || data.message)) || `Request failed (${res.status})`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return { data, status: res.status };
}

/* ------------------------------------------------------------------ the signed-in visitor */

// Same key the Blox web app uses; user 1 is the platform account and means "not signed in".
const USER_KEY = "user_id";
export const GUEST_USER_ID = 1;
export const getUserId = () => {
  try { return Number(localStorage.getItem(USER_KEY)) || GUEST_USER_ID; } catch { return GUEST_USER_ID; }
};
export const setUserId = (id) => { try { localStorage.setItem(USER_KEY, String(id)); } catch { /* private mode */ } };

/* ------------------------------------------------------------------ endpoints */

export const api = {
  // helpers
  decrypt: (encrypted_string) => post("/helpers/decrypt_string_web/", { encrypted_string }),
  encrypt: (string) => post("/helpers/encrypt_string_web/", { string }),
  checkoutSession: (body) => post("/helpers/checkout_session/", body),

  // events
  // Public event list (paged, 10 per page): { data: [...], total_count }.
  events: (page = 1, user_id = 1) => post("/event/web/fetch_events/", { page, user_id }),
  eventDetails: (event_id, user_id) => post("/event/web/event_details/", { event_id, user_id }),
  comments: (event_id, page = 1, limit = 10) => post("/event/web/comment_list/", { event_id, page, limit }),
  postComment: (body) => post("/event/web/event_comment/", body),
  markInterested: (user_id, event_id) => post("/event/web/mark_interested/", { user_id, event_id }),
  register: (registrations) => post("/event/web/event_registration/", { registrations }),
  promos: () => post("/event/web/fetch_promo/"),
  ticket: (event_id, user_id) => post("/event/web/event_ticket/", { event_id, user_id }),
  // Passes and booking questions for an event (and the visitor's pass, if they have one).
  bookingOptions: (event_id, user_id) => post("/event/web/booking_options/", { event_id, user_id }),
  // Gender-balance waitlist: join -> { waitlist: { status, position } }.
  joinWaitlist: (event_id, user_id, ticket_id) => post("/event/web/join_waitlist/", { event_id, user_id, ticket_id }),
  leaveWaitlist: (event_id, user_id) => post("/event/web/leave_waitlist/", { event_id, user_id }),
  // Group passes: info about a shared group link, and claiming a seat in it.
  groupInfo: (code, user_id) => post("/event/web/group_info/", { code, user_id }),
  claimGroup: (code, user_id, answers) => post("/event/web/claim_group/", { code, user_id, answers, agree_conduct: true }),

  // public communities (bloxsocial.live/communities)
  publicCommunities: (page = 1, search = "") => post("/orbits/web/public_communities/", { page, search }),
  publicCommunityFeed: (orbit_id, page = 1) => post("/orbits/web/public_feed/", { orbit_id, page }),

  // shared plans (bloxsocial.live/plan/:id)
  publicPlan: (plan_id) => post("/plans/public/", { plan_id }),
  // live plans within 25 km of a point (bloxsocial.live/plans)
  nearbyPlans: (lat, lng) => post("/plans/public/nearby/", { lat, lng }),

  // profile
  userProfile: (user_id) => post("/profile/web/user_details/", { user_id }),
  // Phone verification by WhatsApp OTP (needed before paying): send -> {id, destination_hint}, verify -> {status}.
  sendPhoneCode: (user_id, phone) => post("/profile/phone/send_code/", { user_id, phone }),
  verifyPhoneCode: (user_id, id, phone, code) => post("/profile/phone/verify_code/", { user_id, id, phone, code }),
  // Profile photo (goes on the event pass).
  uploadProfilePhoto: (user_id, file) => {
    const form = new FormData();
    form.append("user_id", String(user_id));
    form.append("file", file);
    return post("/auth/web/upload_profile/", form);
  },
  // ID verification: front + back of a college / work ID, tied to an entry in their education or work history.
  submitIdVerification: (user_id, front, back, { college_id = 1, organization_id = 1, highschool_id = 1 } = {}) => {
    const form = new FormData();
    form.append("user_id", String(user_id));
    form.append("user_front_page", front);
    form.append("user_back_page", back);
    form.append("college_id", String(college_id));
    form.append("organization_id", String(organization_id));
    form.append("highschool_id", String(highschool_id));
    return post("/profile/verify_profile/", form);
  },

  // auth
  checkEmail: (email) => post("/auth/web/check_email/", { email }),
  authenticate: (email, password) => post("/auth/web/auth_user/", { email, password }),
  login: (user_id, auth_type = "email") =>
    post("/auth/web/user_login/", { user_id, device_token: "web-device-token", device_location: "web", auth_type }),
  createUser: (payload) => post("/auth/web/create_user/", payload),

  // masters
  interests: () => post("/masters/web/fetch_interest/", { search: "" }),
  colleges: () => post("/masters/web/fetch_college/", { search: "" }),
  courses: () => post("/masters/web/fetch_course/", { search: "" }),
  highSchools: () => post("/masters/web/fetch_highschools/", { search: "" }),
  streams: () => post("/masters/web/fetch_streams/", { search: "" }),
  organizations: () => post("/masters/web/fetch_organizations/", { search: "" }),
  createCollege: (body) => post("/masters/web/create_college/", body),
  createCourse: (body) => post("/masters/web/create_course/", body),
  createHighSchool: (body) => post("/masters/web/create_highschool/", body),
  createOrganization: (body) => post("/masters/web/create_organization/", body),
};

/** "0xFFRRGGBB" (Flutter style) or "#RRGGBB" → CSS colour. */
export const toCssColor = (value) => {
  if (!value) return undefined;
  const hex = String(value).replace(/^0x/i, "").replace(/^#/, "");
  return hex.length === 8 ? `#${hex.slice(2)}` : hex.length === 6 ? `#${hex}` : undefined;
};

export const cn = (...classes) => classes.filter(Boolean).join(" ");

export const isGiggleNull = (value) => !value || value === "giggle-null" || String(value).trim() === "";
